import { firebaseDefaults } from "./firebaseDefaults";

type AppEnvironment = "test" | "production";

type FirebaseConfigShape = {
  apiKey?: string;
  authDomain?: string;
  projectId?: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId?: string;
  measurementId?: string;
};

const required = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID"
] as const;

function clean(value: unknown): string {
  return String(value ?? "")
    .replace(/^\uFEFF/, "")
    .trim()
    .replace(/^(["'])(.*)\1$/, "$2")
    .trim();
}

function raw(name: string): string {
  return clean(import.meta.env[name]);
}

function readJsonFallback(): FirebaseConfigShape {
  const encoded = raw("VITE_FIREBASE_CONFIG_JSON");
  if (!encoded) return {};
  try {
    const parsed = JSON.parse(encoded) as FirebaseConfigShape;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function firebaseFieldFor(name: string): keyof FirebaseConfigShape | null {
  switch (name) {
    case "VITE_FIREBASE_API_KEY":
      return "apiKey";
    case "VITE_FIREBASE_AUTH_DOMAIN":
      return "authDomain";
    case "VITE_FIREBASE_PROJECT_ID":
      return "projectId";
    case "VITE_FIREBASE_STORAGE_BUCKET":
      return "storageBucket";
    case "VITE_FIREBASE_MESSAGING_SENDER_ID":
      return "messagingSenderId";
    case "VITE_FIREBASE_APP_ID":
      return "appId";
    case "VITE_FIREBASE_MEASUREMENT_ID":
      return "measurementId";
    default:
      return null;
  }
}

const jsonFallback = readJsonFallback();

function read(name: string): string {
  const direct = raw(name);
  if (direct) return direct;

  const field = firebaseFieldFor(name);
  if (!field) return "";

  const jsonValue = clean(jsonFallback[field]);
  if (jsonValue) return jsonValue;
  return clean(firebaseDefaults[field]);
}

export const appEnvironment: AppEnvironment =
  read("VITE_APP_ENV") === "production" ? "production" : "test";

export const firebaseEnv = {
  apiKey: read("VITE_FIREBASE_API_KEY"),
  authDomain: read("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId: read("VITE_FIREBASE_PROJECT_ID"),
  storageBucket: read("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: read("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  appId: read("VITE_FIREBASE_APP_ID"),
  measurementId: read("VITE_FIREBASE_MEASUREMENT_ID") || undefined
};

// Test and Production must never silently share a Firebase project.
const expectedFirebaseProject = appEnvironment === "production" ? "mundely-47cc8" : "tripai-test-ccb16";
if (firebaseEnv.projectId !== expectedFirebaseProject) {
  throw new Error("MUNDELY_FIREBASE_ENVIRONMENT_MISMATCH");
}
export const functionsRegion = read("VITE_FIREBASE_FUNCTIONS_REGION") || "europe-west1";
export const webBaseUrl = read("VITE_WEB_BASE_URL") || window.location.origin;

export const supportEnv = {
  websiteUrl: read("VITE_MUNDELY_WEBSITE_URL"),
  faqUrl: read("VITE_MUNDELY_FAQ_URL"),
  supportEmail: read("VITE_MUNDELY_SUPPORT_EMAIL"),
  termsUrl: read("VITE_MUNDELY_TERMS_URL"),
  privacyUrl: read("VITE_MUNDELY_PRIVACY_URL"),
  instagramUrl: read("VITE_MUNDELY_INSTAGRAM_URL"),
  facebookUrl: read("VITE_MUNDELY_FACEBOOK_URL"),
  tiktokUrl: read("VITE_MUNDELY_TIKTOK_URL")
};

export function validateEnvironment(): string[] {
  return required.filter((key) => !read(key));
}
